import { hskBands, hskBandRank } from "../content/hsk/hsk-band.js";
import type { SegmentedWord } from "./segmented-words.js";

/**
 * The orders the word list can be read in, and what each of them sorts by.
 *
 * Apart from `segmented-words.ts`, which turns a text into the words it holds.
 * This is a question about a list. The same words come back in a different
 * sequence, and nothing has to be read again for it.
 */

/** How the list can be ordered. */
export type WordOrder =
  | "count"
  | "frequency"
  | "appearance"
  | "length"
  | "band";

/**
 * How many characters a word is written with.
 *
 * Code points rather than UTF-16 units, which is the same unit `Segment.at`
 * counts in and the same unit `characters` in `src/content/chinese/hanzi-slug.ts`
 * settled on for the same reason. 𠮷 is a surname and one character.
 *
 * The lint rule turned off here is about grapheme clusters and is right in
 * general. It cannot reach this: the input is a word the dictionary holds a
 * key for, so there are no emoji and no combining accents in it.
 */
function characterCount(word: string): number {
  // oxlint-disable-next-line typescript/no-misused-spread -- code points are the unit, deliberately: see above
  return [...word].length;
}

/**
 * The comparison each order sorts by, before ties are broken.
 *
 * Each returns a negative number where the first word comes first, which is
 * what `toSorted` wants. Frequency descends, since a reader asking to see the
 * commonest words means the commonest first, and a word with no entry sorts to
 * the bottom rather than the top. See {@link bandRank} for the one that goes
 * the other way.
 */
const comparisons: Readonly<
  Record<WordOrder, (one: SegmentedWord, other: SegmentedWord) => number>
> = {
  count: (one, other) => other.count - one.count,
  frequency: (one, other) => (other.frequency ?? -1) - (one.frequency ?? -1),
  appearance: (one, other) => one.firstAt - other.firstAt,
  length: (one, other) => characterCount(other.text) - characterCount(one.text),
  band: (one, other) => bandRank(one) - bandRank(other),
};

/**
 * How far into the HSK scale a word sits, ungraded words last.
 *
 * The one order that ascends. A reader ordering by band is asking what to
 * learn first, and that is HSK 1. A word the syllabus does not grade sorts
 * below every word it does, rather than above them at rank nought.
 */
function bandRank(word: SegmentedWord): number {
  return word.band === undefined ? hskBands.length : hskBandRank(word.band);
}

/**
 * The list in one of its orders, with every tie broken the same way.
 *
 * **Ties are the common case rather than an edge.** Most words in a text
 * appear once, so ordering by count leaves nearly the whole list tied, and the
 * sixteen frequency levels resolve to eleven over the 264 distinct words in
 * 1,000 characters. An unbroken tie is the order a `Map` happened to hold,
 * which shifts under an edit somewhere else in the text and makes the list
 * look like it is shuffling itself.
 *
 * So a tie falls back to how often the text uses the word, and then to where
 * it first appeared, which is unique. Every order is therefore total, and a
 * word keeps its place while the rest of the text changes around it.
 */
export function orderedWords(
  words: readonly SegmentedWord[],
  order: WordOrder,
): readonly SegmentedWord[] {
  const compare = comparisons[order];

  return words.toSorted(
    (one, other) =>
      compare(one, other) ||
      other.count - one.count ||
      one.firstAt - other.firstAt,
  );
}
